/**
 * Nav badge context — the "shell's badge hook" WSRPDM-0012 wires the Tenants
 * roster count through (WSRPDM-0011 left `NavItem.badge` inert; this is the
 * live-count plumbing that fills it in for `/tenants`, `/queue`,
 * `/notifications` as each screen adopts it).
 *
 * A screen calls `useSetNavBadge('tenants', tenants.length)`; AppLayout reads
 * `useNavBadges()` to render the count next to the matching nav item. Kept as
 * plain context (not a data store) — screens still own their own fetching,
 * this just publishes a number outward for the shell to render.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { NavBadgeKey } from '@/components/nav';

type BadgeCounts = Partial<Record<NavBadgeKey, number>>;

interface NavBadgeContextValue {
  counts: BadgeCounts;
  setBadge: (key: NavBadgeKey, count: number | null) => void;
}

const NavBadgeContext = createContext<NavBadgeContextValue | null>(null);

export function NavBadgeProvider({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState<BadgeCounts>({});

  const setBadge = useCallback((key: NavBadgeKey, count: number | null) => {
    setCounts((prev) => {
      if (count === null) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === count) return prev;
      return { ...prev, [key]: count };
    });
  }, []);

  const value = useMemo<NavBadgeContextValue>(() => ({ counts, setBadge }), [counts, setBadge]);

  return <NavBadgeContext.Provider value={value}>{children}</NavBadgeContext.Provider>;
}

function useNavBadgeContext(): NavBadgeContextValue {
  const ctx = useContext(NavBadgeContext);
  if (!ctx) throw new Error('useNavBadges/useSetNavBadge must be used within a NavBadgeProvider');
  return ctx;
}

/** AppLayout: read the current badge counts to render next to nav items. */
export function useNavBadges(): BadgeCounts {
  return useNavBadgeContext().counts;
}

/**
 * A screen: publish a live count for its nav badge slot. Syncs with the
 * shell (an external consumer outside this component's own render output),
 * so this is a legitimate effect — publish on mount/change, clear on unmount
 * so a stale count doesn't linger after navigating away.
 */
export function useSetNavBadge(key: NavBadgeKey, count: number | null): void {
  const { setBadge } = useNavBadgeContext();
  useEffect(() => {
    setBadge(key, count);
    return () => setBadge(key, null);
  }, [key, count, setBadge]);
}
